import { useCallback, useMemo, useState } from "react";
import { deleteProject, useSearch } from "@";
import { useProjectsQuery } from "../api/queries/index";
import {
  useToggleActiveMutation,
  useToggleArchiveMutation,
} from "../api/mutations/index";
import { searchValues } from "../lib/options";
import { TableActions, tableColumns } from "../components/TableOptions";
import type {
  PaginationState,
  Project,
  QueryParams,
  SearchValues,
} from "../types";
import type { ColumnDef } from "@tanstack/react-table";

interface UseProjectsPageReturn {
  data: Project[];
  columns: ColumnDef<Project>[];
  isLoading: boolean;
  error: Error | null;
  totalItems: number;
  search: { show: boolean; values: SearchValues };
  updateSearch: (params: { show?: boolean; values?: SearchValues }) => void;
  handleSearchSubmit: () => void;
  handleSearchReset: () => void;
  pg: PaginationState;
  updatePg: (newPg: Partial<PaginationState>) => void;
  refetch: () => void;
}

export const useProjectsPage = (): UseProjectsPageReturn => {
  const { search, updateSearch } = useSearch(searchValues);
  const [pg, setPg] = useState<PaginationState>({ page: 1, size: 10 });
  const [appliedSearch, setAppliedSearch] =
    useState<SearchValues>(searchValues);

  // Query
  const queryParams: QueryParams = useMemo(
    () => ({ page: pg.page, perSize: pg.size, search: appliedSearch }),
    [pg.page, pg.size, appliedSearch],
  );

  const {
    data: queryData,
    isLoading,
    error,
    refetch,
  } = useProjectsQuery(queryParams);

  const data = queryData?.data || [];
  const totalItems = queryData?.totalItems || 0;

  // Mutations
  const toggleActiveMutation = useToggleActiveMutation();
  const toggleArchiveMutation = useToggleArchiveMutation();

  // Handlers
  const updatePg = useCallback((newPg: Partial<PaginationState>) => {
    setPg((prev) => ({ ...prev, ...newPg }));
  }, []);

  const handleSearchSubmit = useCallback(() => {
    setAppliedSearch(search.values);
    setPg((prev) => ({ ...prev, page: 1 }));
  }, [search.values]);

  const handleSearchReset = useCallback(() => {
    updateSearch({ values: searchValues });
    setAppliedSearch(searchValues);
    setPg((prev) => ({ ...prev, page: 1 }));
  }, [updateSearch]);

  const handleDelete = useCallback(
    async (id: number) => {
      const res = await deleteProject(id);
      if (res) await refetch();
    },
    [refetch],
  );

  const handleToggleActive = useCallback(
    (id: number, value: boolean) =>
      toggleActiveMutation.mutateAsync({ id, value }),
    [toggleActiveMutation],
  );

  const handleToggleArchive = useCallback(
    (id: number, value: boolean) =>
      toggleArchiveMutation.mutateAsync({ id, value }),
    [toggleArchiveMutation],
  );

  // Columns with actions (actions first for RTL - appears on right side)
  const columns = useMemo(
    () =>
      [
        ...tableColumns,

        {
          id: "actions",
          header: "الخيارات",
          meta: { grow: 1.5 },
          cell: (info: { row: { original: Project } }) => {
            return (
              <TableActions
                row={info.row.original}
                ToggleActive={handleToggleActive}
                ToggleArchive={handleToggleArchive}
                deleteHandler={handleDelete}
              />
            );
          },
        },
      ] as ColumnDef<Project>[],
    [handleToggleActive, handleToggleArchive, handleDelete],
  );

  return {
    // Data
    data,
    columns,
    isLoading,
    error: error as Error | null,
    totalItems,
    // Search
    search,
    updateSearch,
    handleSearchSubmit,
    handleSearchReset,
    // Pagination
    pg,
    updatePg,
    // Actions
    refetch,
  };
};
